import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {Select, Store} from '@ngxs/store';
import {IUser} from '../../interfaces/user.interface';
import {GetUserNotifications} from '../../states/profile/profile.actions';
import {Observable} from 'rxjs/internal/Observable';
import {ProfileState} from '../../states/profile/profile.state';
import {OrdersState} from '../../states/orders/orders.state';
import {ClearOrdersState, GetUserOrders} from '../../states/orders/orders.actions';
import {Router} from '@angular/router';
import {AuthState} from '../../states/auth/auth.state';
import {LockSystem, Logout, SetPharmacyId} from '../../states/auth/auth.actions';
import {IPharmacy} from '../../interfaces/pharmacy.interface';
import {IPharmacyDetails} from '../../interfaces/pharmacy-details.interface';
import {PharmacyState} from '../../states/pharmacy/pharmacy.state';
import {UserRolesEnum} from '../../interfaces/UserRolesEnum';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BasicSubscriptionPlanGuard } from '../../guards/basic-subscription-plan.guard';
import { ClearPendingTransactions, SetCarouselDisplayList, SetDashboardFilters, SetTransactionsForScroll } from '../../states/transactions/transactions.actions';
import { SetCurrentPharmacy } from '../../states/pharmacy/pharmacy.actions';
import { EventsService } from '../../services/events.service';
import { ClearInventoryState } from '../../states/inventory/inventory.actions';
import { ClearDrugsState } from '../../states/drugs/drugs.actions';
import { ClearUserMessageState } from '../../states/user-message/user-message.actions';
import { UserMessageState } from '../../states/user-message/user-message.state';

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: 'header.component.html',
  styleUrls: ['header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit {
  @Select(AuthState.activeUser) user$: Observable<IUser>;
  @Select(AuthState.activeUserIsLoggedInUser) activeUserIsLoggedInUser$: Observable<any>;
  @Select(OrdersState.userOrders) userOrdersCount$: Observable<number>;
  @Select(ProfileState.notifications) notifications$: Observable<any>;
  @Select(PharmacyState.pharmacies) pharmacies$: Observable<IPharmacy[]>;
  @Select(PharmacyState.currentPharmacy) currentPharmacy$: Observable<IPharmacyDetails>;
  @Select(PharmacyState.backCountEnabled) backCountEnabled$: Observable<boolean>;
  @Select(UserMessageState.unreadTotal) unreadTotal$: Observable<number>;

  unreadTotal = 0;


  @ViewChild('userMenu') userMenu;

  showEditProfileDialog: boolean;

  adminRoles = [UserRolesEnum.GlobalAdmin, UserRolesEnum.CompanyAdmin, UserRolesEnum.PharmacyAdmin];
  isNotBasicPlan: boolean;

  activeUserIsLoggedInUser: boolean;

  constructor(
    private store: Store,
    private router: Router,
    private basicPlanGuard: BasicSubscriptionPlanGuard,
    private eventsService: EventsService,
    private cd: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.isNotBasicPlan = this.basicPlanGuard.canActivate() as boolean;
    if (this.isNotBasicPlan) {
      this.store.dispatch(new GetUserOrders());
    }
    this.store.dispatch(new GetUserNotifications('id'));

    this.activeUserIsLoggedInUser$
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        if (value) {
          this.activeUserIsLoggedInUser = true;
        } else {
          this.activeUserIsLoggedInUser = false;
        }
      });

      this.unreadTotal$
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((val) => {
        if (!isNaN(val)) {
          this.unreadTotal = val;
          this.cd.markForCheck();
        }
      })
  }

  toggleUserMenu(popover): void {
    popover.isOpen() ? popover.close() : popover.open();
  }

  closeMenu(): void {
    this.userMenu.close();
  }

  chooseUserMenuOption(option): void {
    switch (option) {
      case 'messages':
        this.router.navigateByUrl('/messages');
        break;
      case 'profile':
        this.openEditProfileDialog();
        break;
      case 'changePharmacy':
        return;
      case 'logout':
        this.logout();
        break;
    }

    this.closeMenu();
  }

  openEditProfileDialog(): void {
    this.showEditProfileDialog = true;
  }

  closeEditProfileDialog(): void {
    this.showEditProfileDialog = false;
  }

  afterEditProfile(success: boolean): void {
    // TODO: show message about saved status
    this.closeEditProfileDialog();
  }

  switchPharmacy(id: string): void {
    this.prepareStateForTheNewPharmacy();
    this.store.dispatch(new SetPharmacyId(id));
    this.store.dispatch(new SetCurrentPharmacy(id));
    // rather than try and reload each and every
    //  part of the local state, refresh the page
    //  and let the code we've already got handle it.
    this.router.navigate(['']).then(() => {
      window.location.reload();
    });
  }

  lockScreen(): void {
    this.store.dispatch(new LockSystem(true));
  }

  logout(): void {
    this.eventsService.logOutClicked$.next();
    this.store.dispatch(new Logout());
    this.router.navigateByUrl('auth/login');
  }

  onReceiveOrderClick(): void {
    this.userOrdersCount$.pipe(
      map((x) => {
        if (x > 1) {
          this.router.navigate(['inventory/order-history'], {state: {shouldRedirectToNotReceived: true}});
        } else if (x === 1) {
          this.router.navigate(['inventory/receive-order', '']);
        }

      }), untilDestroyed(this))
      .subscribe();
  }

  private prepareStateForTheNewPharmacy(): void {
    this.store.dispatch(new SetCarouselDisplayList([]));
    this.store.dispatch(new ClearPendingTransactions());
    this.store.dispatch(new SetDashboardFilters(null));
    this.store.dispatch(new SetTransactionsForScroll(null));
    this.store.dispatch(new ClearOrdersState());
    this.store.dispatch(new ClearInventoryState());
    this.store.dispatch(new ClearDrugsState());
    this.store.dispatch(new ClearUserMessageState());
  }
}
